import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { t, currentLanguage } from "../../i18n";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import LinkedInFollowButton from "./LinkedInFollowButton";

interface SectionRefs {
  solutions?: React.RefObject<HTMLDivElement | null>;
  insights?: React.RefObject<HTMLDivElement | null>;
  about?: React.RefObject<HTMLDivElement | null>;
  blog?: React.RefObject<HTMLDivElement | null>;
  contact?: React.RefObject<HTMLDivElement | null>;
}

interface HeaderProps {
  sectionRefs?: SectionRefs;
}

// Sayfadaki resimler (ve tercihen fontlar) yerleşene kadar bekle
async function waitForImagesSettled(timeoutMs = 1500) {
  const imgs = Array.from(document.images).filter((img) => !img.complete);
  const fontsReady = (document as any).fonts?.ready?.catch?.(() => undefined);

  if (imgs.length === 0) {
    // Fontlar varsa beklemeyi de ekleyelim (opsiyonel, destek yoksa sorun değil)
    if (fontsReady) await Promise.race([fontsReady, new Promise((r) => setTimeout(r, timeoutMs))]);
    return;
  }

  await new Promise<void>((resolve) => {
    let left = imgs.length;
    const done = () => {
      left -= 1;
      if (left <= 0) resolve();
    };
    const timer = setTimeout(() => resolve(), timeoutMs); // emniyet süresi

    imgs.forEach((img) => {
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  });

  if (fontsReady) {
    // Fontlar hazırsa bekle (opsiyonel)
    await Promise.race([fontsReady, new Promise((r) => setTimeout(r, timeoutMs))]);
  }
}

function scrollToEl(el: Element, behavior: ScrollBehavior = "smooth") {
  (el as HTMLElement).scrollIntoView({ behavior, block: "start" });
}

const Header: React.FC<HeaderProps> = ({ sectionRefs }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cleanupRef = useRef<() => void>(() => {});

  // Header gölge durumu
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Bileşen unmount'ta temizlik
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  // URL hash'e göre otomatik scroll (resimler/ fontlar yerleşince)
  useEffect(() => {
    const hash = location.hash;
    if (!(hash && hash.startsWith("#"))) return;

    const sectionId = hash.substring(1);

    const pick = () =>
      sectionId === "contact" ? (document.querySelector("[data-contact-section]") as Element | null) || document.getElementById("contact") : document.getElementById(sectionId);

    const el = pick();
    if (!el) return;

    let cancelled = false;
    cleanupRef.current = () => {
      cancelled = true;
    };

    (async () => {
      await waitForImagesSettled();
      if (!cancelled) {
        // CSS'te section'lara scroll-mt verdiğimiz için offset hesabına gerek kalmıyor
        scrollToEl(el);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.hash]);

  const handleNavigation = (sectionId: keyof SectionRefs) => {
    // Single-page bölümler: sadece hash'e git, scroll işini effect yapacak
    if (sectionId === "solutions" || sectionId === "insights") {
      navigate(`/${currentLanguage}/#${sectionId}`);
      setMenuOpen(false);
      return;
    }

    // Diğer sayfalar
    if (sectionId === "blog") {
      navigate(`/${currentLanguage}/blog`);
    } else if (sectionId === "about") {
      navigate(`/${currentLanguage}/our-mission`);
    }
    setMenuOpen(false);
  };

  const handleContactScroll = async () => {
    // Önce URL'yi güncelle
    navigate(`/${currentLanguage}/#contact`);
    setMenuOpen(false);

    // Eğer aynı sayfadaysak, doğrudan scroll yap
    if (location.pathname === `/${currentLanguage}` || location.pathname === `/${currentLanguage}/`) {
      const contactElement = (document.querySelector("[data-contact-section]") as Element | null) || document.getElementById("contact");
      if (contactElement) {
        await waitForImagesSettled();
        scrollToEl(contactElement);
      }
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (sectionRefs) {
      scrollToTop();
    } else {
      navigate(`/${currentLanguage}`);
    }
    // URL'den hash'i temizle
    window.history.replaceState(null, "", `/${currentLanguage}`);
    setMenuOpen(false);
  };

  return (
    <header className={`bg-white text-black w-full top-0 z-50 sticky ${isScrolled ? "shadow-md" : ""}`}>
      <div className="max-w-8xl mx-auto px-6 md:px-20 py-3 relative flex items-center justify-between">
        {/* Sol taraf: logo + menü */}
        <div className="flex items-center space-x-8">
          {/* Logo */}
          <div className="cursor-pointer flex items-center" onClick={handleLogoClick} style={{ height: "64px" }}>
            <img src="/images/sensvise-final-h-blue.svg" alt="Senswise Logo" className="h-12 object-contain mt-[-20px]" />
          </div>

          {/* Menü - sadece md ve üstü için */}
          <div className="hidden md:flex items-center space-x-6 text-sm">
            <a
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("solutions");
              }}
              href="#solutions"
              className="cursor-pointer focus-links text-black leading-none"
            >
              {t("navigation.solutions")}
            </a>
            <a
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("insights");
              }}
              href="#insights"
              className="cursor-pointer focus-links text-black leading-none"
            >
              {t("navigation.insights")}
            </a>
            <a onClick={() => handleNavigation("about")} href={`/${currentLanguage}/our-mission`} className="cursor-pointer focus-links text-black leading-none">
              {t("navigation.about")}
            </a>
            <a onClick={() => handleNavigation("blog")} href={`/${currentLanguage}/blog`} className="cursor-pointer focus-links text-black leading-none">
              {t("navigation.blog")}
            </a>
          </div>
        </div>

        {/* Sağ taraf: LinkedIn Follow, Dil değiştirici, Giriş ve Buton */}
        <div className="ml-5 hidden md:flex items-center space-x-6">
          <LinkedInFollowButton />
          <a href="https://app.senswise.co/" className="cursor-pointer focus-links font-semibold text-black text-sm leading-none">
            {t("navigation.login")}
          </a>
          <button onClick={handleContactScroll} className="text-white px-4 py-2.5 rounded-xl text-sm leading-none bg-primaryGreen">
            {t("navigation.demoRequest")}
          </button>
          <LanguageSwitcher />
        </div>

        {/* Hamburger - Mobil görünüm */}
        <div className="md:hidden flex items-center gap-3">
          <LinkedInFollowButton />
          <div className="flex flex-col justify-between w-6 h-5 cursor-pointer z-50" onClick={() => setMenuOpen((prev) => !prev)}>
            <span className="block h-0.5 bg-black rounded"></span>
            <span className="block h-0.5 bg-black rounded"></span>
            <span className="block h-0.5 bg-black rounded"></span>
          </div>
        </div>

        {/* Mobil Menü */}
        <nav className={`${menuOpen ? "flex" : "hidden"} absolute top-full left-0 w-full bg-white flex-col items-center py-6 space-y-4 text-sm shadow-lg md:hidden`}>
          <LanguageSwitcher />
          <a onClick={() => handleNavigation("solutions")} href="#solutions" className="cursor-pointer focus-links text-black">
            {t("navigation.solutions")}
          </a>
          <a onClick={() => handleNavigation("insights")} href="#insights" className="cursor-pointer focus-links text-black">
            {t("navigation.insights")}
          </a>
          <a onClick={() => handleNavigation("about")} href={`/${currentLanguage}/our-mission`} className="cursor-pointer focus-links text-black">
            {t("navigation.about")}
          </a>
          <a onClick={() => handleNavigation("blog")} href={`/${currentLanguage}/blog`} className="cursor-pointer focus-links text-black">
            {t("navigation.blog")}
          </a>
          <a href="https://app.senswise.co/" className="cursor-pointer focus-links text-black">
            {t("navigation.login")}
          </a>
          <button onClick={handleContactScroll} className="text-white px-4 py-2.5 rounded-xl text-sm leading-none bg-primaryGreen">
            {t("navigation.talkToTeam")}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
